import type { ArraySchema, BaseSchema, InferOutput, SafeParseResult } from "valibot"
import {
  ValiError,
  flatten as valibotFlatten,
  parse as valibotParse,
  safeParse as valibotSafeParse,
} from "valibot"

import { ApiServiceSingleton } from "./api-service-singleton"
import { KyAxiosWrapper } from "./ky-axios-wrapper"

export class BaseApiService {
  protected instance: KyAxiosWrapper = {} as KyAxiosWrapper

  constructor() {
    // nextTick is used to prevent SSR errors. but it still works in SSR
    nextTick(() => {
      this.instance = ApiServiceSingleton.instance()
    })
  }

  protected getData(res: any) {
    return res.data.data
  }

  protected safeParse<S extends BaseSchema<any, any, any> | ArraySchema<BaseSchema<any, any, any>, any>>(
    res: any,
    schema: S
  ) {
    return valibotSafeParse(schema, res, {
      abortEarly: false,
      abortPipeEarly: false,
    }) as SafeParseResult<S> & { output: InferOutput<S> }
  }

  protected parse<S extends BaseSchema<any, any, any> | ArraySchema<BaseSchema<any, any, any>, any>>(
    res: any,
    schema: S,
    errorMessage: string = "Invalid api response"
  ): InferOutput<S> {
    try {
      return valibotParse(schema, res)
    } catch (err) {
      if (err instanceof ValiError) {
        console.error(errorMessage, valibotFlatten<S>(err.issues))
      }
      throw err
    }
  }

  protected parseParams<S extends BaseSchema<any, any, any> | ArraySchema<BaseSchema<any, any, any>, any>>(
    res: any,
    schema: S
  ): InferOutput<S> {
    return this.parse(res, schema, "Invalid api params")
  }

  protected parsePaginated<S extends BaseSchema<any, any, any> | ArraySchema<BaseSchema<any, any, any>, any>>(
    res: any,
    schema: S
  ): PaginationResponse<InferOutput<S>> {
    try {
      return {
        data: valibotParse(schema, res.data) as S[],
        paging: res.paging,
        groups: res.groups,
        additional: res.additional,
      }
    } catch (err) {
      if (err instanceof ValiError) {
        console.error("Invalid api response", valibotFlatten<S>(err.issues), { res })
      }
      throw err
    }
  }
}
